/**
 * useGameData — async fetch of all game data from the API.
 * Data is fetched once per browser session (module-level singleton cache).
 * `speciesId` in TeamMember uses the pokemon `slug` field.
 *
 * This is the one place game data (moves/abilities/items/natures/pokemon) is
 * fetched and cached — contexts/GameDataContext.tsx wraps this same singleton
 * rather than fetching independently.
 */
import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/env';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface GamePokemon {
  slug: string;
  name: string;
  dex_number: number;
  forme: string | null;
  is_legendary: boolean;
  /** null means genderless */
  gender_male: number | null;
  gender_female: number | null;
  types: string[];
  hp: number;
  atk: number;
  def: number;
  spa: number;
  spd: number;
  spe: number;
  ability1: string | null;
  ability2: string | null;
  hidden_ability: string | null;
}

export interface GameMove {
  slug: string;
  name: string;
  type: string;
  category: string;
  power: number | null;
  accuracy: number | null;
  pp: number;
  description: string;
  flags?: string[];
}

export interface GameNature {
  name: string;
  /** DB stat column name: 'attack', 'defense', 'sp_attack', 'sp_defense', 'speed' */
  stat_up: string | null;
  stat_down: string | null;
}

export interface GameAbility {
  name: string;
  slug: string;
  description: string;
}

export interface GameItem {
  name: string;
  slug: string;
  description: string;
  // DB-backed classification flags (backend/src/services/dex.ts getItems) —
  // power the Items page's category filter. No name-guessing: each of these
  // mirrors a real column PRO's own item data already has.
  isBerry: boolean;
  isGem: boolean;
  isPokeball: boolean;
  isChoice: boolean;
  isZCrystal: boolean;
  isMegaStone: boolean;
  isPlate: boolean;
  isMemory: boolean;
  isDrive: boolean;
}

export interface LearnsetMoveEntry {
  move: string;
  type: string;
  category: string;
  power: number | null;
  accuracy: number | null;
  pp: number;
  description: string;
  location?: string[] | null;
}

export interface LearnsetLevelEntry extends LearnsetMoveEntry {
  level: number;
}

export interface LearnsetData {
  level_up: LearnsetLevelEntry[];
  tms: LearnsetMoveEntry[];
  move_tutors: LearnsetMoveEntry[];
  egg_moves: LearnsetMoveEntry[];
}

export interface TypeEntry {
  english: string;
  effective: string[];
  ineffective: string[];
  no_effect: string[];
}

export interface GameData {
  pokemon: GamePokemon[];
  pokemonBySlug: Map<string, GamePokemon>;
  /** keyed by lowercase name */
  pokemonByName: Map<string, GamePokemon>;
  moves: GameMove[];
  movesByName: Map<string, GameMove>;
  /** ability/item names only, for autocomplete-style consumers */
  abilities: string[];
  items: string[];
  /** full ability/item objects (with description), keyed by name */
  abilitiesByName: Map<string, GameAbility>;
  itemsByName: Map<string, GameItem>;
  natures: GameNature[];
  naturesMap: Map<string, GameNature>;
  typeChart: TypeEntry[];
}

// ── Static type chart ─────────────────────────────────────────────────────────
// Standard gen 6+ type effectiveness — static game mechanics, no DB needed.

export const TYPE_CHART: TypeEntry[] = [
  { english: 'Normal',   effective: [],                                          ineffective: ['Rock', 'Steel'],                                    no_effect: ['Ghost']  },
  { english: 'Fighting', effective: ['Normal', 'Ice', 'Rock', 'Dark', 'Steel'],  ineffective: ['Poison', 'Flying', 'Psychic', 'Bug', 'Fairy'],      no_effect: ['Ghost']  },
  { english: 'Flying',   effective: ['Grass', 'Fighting', 'Bug'],                ineffective: ['Electric', 'Rock', 'Steel'],                        no_effect: []         },
  { english: 'Poison',   effective: ['Grass', 'Fairy'],                          ineffective: ['Poison', 'Ground', 'Rock', 'Ghost'],                no_effect: ['Steel']  },
  { english: 'Ground',   effective: ['Fire', 'Electric', 'Poison', 'Rock', 'Steel'], ineffective: ['Grass', 'Bug'],                                no_effect: ['Flying'] },
  { english: 'Rock',     effective: ['Fire', 'Ice', 'Flying', 'Bug'],            ineffective: ['Fighting', 'Ground', 'Steel'],                      no_effect: []         },
  { english: 'Bug',      effective: ['Grass', 'Psychic', 'Dark'],                ineffective: ['Fire', 'Fighting', 'Flying', 'Ghost', 'Steel', 'Fairy'], no_effect: []    },
  { english: 'Ghost',    effective: ['Psychic', 'Ghost'],                        ineffective: ['Dark'],                                             no_effect: ['Normal'] },
  { english: 'Steel',    effective: ['Ice', 'Rock', 'Fairy'],                    ineffective: ['Fire', 'Water', 'Electric', 'Steel'],               no_effect: []         },
  { english: 'Fire',     effective: ['Grass', 'Ice', 'Bug', 'Steel'],            ineffective: ['Fire', 'Water', 'Rock', 'Dragon'],                  no_effect: []         },
  { english: 'Water',    effective: ['Fire', 'Ground', 'Rock'],                  ineffective: ['Water', 'Grass', 'Dragon'],                         no_effect: []         },
  { english: 'Grass',    effective: ['Water', 'Ground', 'Rock'],                 ineffective: ['Fire', 'Grass', 'Poison', 'Flying', 'Bug', 'Dragon', 'Steel'], no_effect: [] },
  { english: 'Electric', effective: ['Water', 'Flying'],                         ineffective: ['Electric', 'Grass', 'Dragon'],                      no_effect: ['Ground'] },
  { english: 'Psychic',  effective: ['Fighting', 'Poison'],                      ineffective: ['Psychic', 'Steel'],                                 no_effect: ['Dark']   },
  { english: 'Ice',      effective: ['Grass', 'Ground', 'Flying', 'Dragon'],     ineffective: ['Water', 'Ice'],                                     no_effect: []         },
  { english: 'Dragon',   effective: ['Dragon'],                                  ineffective: ['Steel'],                                            no_effect: ['Fairy']  },
  { english: 'Dark',     effective: ['Psychic', 'Ghost'],                        ineffective: ['Fighting', 'Dark', 'Fairy'],                        no_effect: []         },
  { english: 'Fairy',    effective: ['Fighting', 'Dragon', 'Dark'],              ineffective: ['Fire', 'Poison', 'Steel'],                          no_effect: []         },
];

// ── Module-level singleton cache ───────────────────────────────────────────────

let _cache: GameData | null = null;
let _loading: Promise<GameData> | null = null;

// Raw API payloads, cached in localStorage so a reload/return visit is instant
// instead of re-fetching 5 endpoints (the calc's "Loading Pokémon data" wait).
interface RawBundle { pokemon: any[]; moves: any[]; abilities: any[]; items: any[]; natures: any[] }
const GD_CACHE_KEY = 'gd:raw:v1';        // bump the vN when the mapping below changes
const GD_TTL_MS = 24 * 60 * 60 * 1000;   // 24h — game data changes rarely

async function fetchRaw(): Promise<RawBundle> {
  const [pokemon, moves, abilities, items, natures] = await Promise.all([
    fetch(`${API_BASE_URL}/dex/pokemon/all`).then(r => { if (!r.ok) throw new Error('pokemon/all failed'); return r.json() as Promise<any[]>; }),
    fetch(`${API_BASE_URL}/dex/moves`).then(r => { if (!r.ok) throw new Error('moves failed'); return r.json() as Promise<any[]>; }),
    fetch(`${API_BASE_URL}/dex/abilities`).then(r => { if (!r.ok) throw new Error('abilities failed'); return r.json() as Promise<any[]>; }),
    fetch(`${API_BASE_URL}/dex/items`).then(r => { if (!r.ok) throw new Error('items failed'); return r.json() as Promise<any[]>; }),
    fetch(`${API_BASE_URL}/dex/natures`).then(r => { if (!r.ok) throw new Error('natures failed'); return r.json() as Promise<any[]>; }),
  ]);
  return { pokemon, moves, abilities, items, natures };
}

function readRawCache(): RawBundle | null {
  try {
    const s = localStorage.getItem(GD_CACHE_KEY);
    if (!s) return null;
    const { t, raw } = JSON.parse(s);
    if (!raw || Date.now() - t > GD_TTL_MS) return null;
    return raw as RawBundle;
  } catch { return null; }
}

function writeRawCache(raw: RawBundle): void {
  try { localStorage.setItem(GD_CACHE_KEY, JSON.stringify({ t: Date.now(), raw })); } catch { /* quota/full — skip */ }
}

function buildGameData(bundle: RawBundle): GameData {
  const { pokemon: pokemonRaw, moves: movesRaw, abilities: abilitiesRaw, items: itemsRaw, natures: naturesRaw } = bundle;

  const pokemon: GamePokemon[] = pokemonRaw.map((r: any) => ({
    slug:         r.slug,
    name:         r.name,
    dex_number:   r.dex_number,
    forme:        r.forme ?? null,
    is_legendary: !!r.is_legendary,
    gender_male:  r.gender_male  ?? null,
    gender_female: r.gender_female ?? null,
    types:        r.types ? (typeof r.types === 'string' ? r.types.split(',') : r.types) : [],
    hp: r.hp ?? 0, atk: r.atk ?? 0, def: r.def ?? 0,
    spa: r.spa ?? 0, spd: r.spd ?? 0, spe: r.spe ?? 0,
    ability1:      r.ability1      ?? null,
    ability2:      r.ability2      ?? null,
    hidden_ability: r.hidden_ability ?? null,
  }));

  const pokemonBySlug = new Map(pokemon.map(p => [p.slug, p]));
  const pokemonByName = new Map(pokemon.map(p => [p.name.toLowerCase(), p]));

  const moves: GameMove[] = movesRaw.map((r: any) => ({
    slug:        r.slug,
    name:        r.name,
    type:        r.type,
    category:    r.category,
    power:       r.power    ?? null,
    accuracy:    r.accuracy ?? null,
    pp:          r.pp       ?? 5,
    description: r.description ?? '',
    flags:       Array.isArray(r.flags) ? r.flags : [],
  }));
  const movesByName = new Map(moves.map(m => [m.name, m]));

  const abilitiesFull: GameAbility[] = abilitiesRaw.map((a: any) => ({
    name: a.name, slug: a.slug, description: a.description ?? '',
  }));
  const itemsFull: GameItem[] = itemsRaw.map((i: any) => ({
    name: i.name, slug: i.slug, description: i.description ?? '',
    isBerry: !!i.isBerry, isGem: !!i.isGem, isPokeball: !!i.isPokeball, isChoice: !!i.isChoice,
    isZCrystal: !!i.isZCrystal, isMegaStone: !!i.isMegaStone,
    isPlate: !!i.isPlate, isMemory: !!i.isMemory, isDrive: !!i.isDrive,
  }));
  const abilities: string[] = abilitiesFull.map(a => a.name).sort();
  const items: string[]     = itemsFull.map(i => i.name).sort();
  const abilitiesByName = new Map(abilitiesFull.map(a => [a.name, a]));
  const itemsByName     = new Map(itemsFull.map(i => [i.name, i]));

  const natures: GameNature[] = naturesRaw.map((n: any) => ({
    name:      n.name,
    stat_up:   n.stat_up   ?? null,
    stat_down: n.stat_down ?? null,
  }));
  const naturesMap = new Map(natures.map(n => [n.name, n]));

  return {
    pokemon, pokemonBySlug, pokemonByName,
    moves, movesByName,
    abilities, items,
    abilitiesByName, itemsByName,
    natures, naturesMap,
    typeChart: TYPE_CHART,
  };
}

// ── Learnset cache (per pokemon slug) ──────────────────────────────────────────

const _learnsetCache = new Map<string, LearnsetData | null>();
const _learnsetInFlight = new Map<string, Promise<LearnsetData | null>>();

export function getLearnset(slug: string): Promise<LearnsetData | null> {
  if (_learnsetCache.has(slug)) {
    return Promise.resolve(_learnsetCache.get(slug)!);
  }
  if (_learnsetInFlight.has(slug)) {
    return _learnsetInFlight.get(slug)!;
  }

  const promise = fetch(`${API_BASE_URL}/dex/pokemon/${encodeURIComponent(slug)}/learnset`)
    .then(r => { if (!r.ok) throw new Error('learnset failed'); return r.json() as Promise<LearnsetData>; })
    .then(data => {
      _learnsetCache.set(slug, data);
      _learnsetInFlight.delete(slug);
      return data;
    })
    .catch(() => {
      _learnsetCache.set(slug, null);
      _learnsetInFlight.delete(slug);
      return null;
    });

  _learnsetInFlight.set(slug, promise);
  return promise;
}

// ── Learnset history cache (per pokemon slug — all generations at once) ───────
// Distinct from getLearnset above: that's the single live, battle-legal
// ruleset (Team Builder legality etc.); this is the admin-generated, per-
// generation reference view (services/learnsets/ on the backend) — Bulbapedia-
// style "pick a gen" browsing, not tied to what's currently legal in PRO.

export type LearnsetHistory = Record<number, LearnsetData>; // genId (1-9) -> that gen's learnset

const _learnsetHistoryCache = new Map<string, LearnsetHistory | null>();
const _learnsetHistoryInFlight = new Map<string, Promise<LearnsetHistory | null>>();

export function getLearnsetHistory(slug: string): Promise<LearnsetHistory | null> {
  if (_learnsetHistoryCache.has(slug)) {
    return Promise.resolve(_learnsetHistoryCache.get(slug)!);
  }
  if (_learnsetHistoryInFlight.has(slug)) {
    return _learnsetHistoryInFlight.get(slug)!;
  }

  const promise = fetch(`${API_BASE_URL}/dex/pokemon/${encodeURIComponent(slug)}/learnset/history`)
    .then(r => { if (!r.ok) throw new Error('learnset history failed'); return r.json() as Promise<LearnsetHistory>; })
    .then(data => {
      _learnsetHistoryCache.set(slug, data);
      _learnsetHistoryInFlight.delete(slug);
      return data;
    })
    .catch(() => {
      _learnsetHistoryCache.set(slug, null);
      _learnsetHistoryInFlight.delete(slug);
      return null;
    });

  _learnsetHistoryInFlight.set(slug, promise);
  return promise;
}

export function loadGameData(): Promise<GameData> {
  if (_cache) return Promise.resolve(_cache);
  if (_loading) return _loading;

  // Instant path: build from the localStorage bundle if we have a fresh one,
  // then refresh it in the background so the next load stays current.
  const cachedRaw = readRawCache();
  if (cachedRaw) {
    _cache = buildGameData(cachedRaw);
    fetchRaw().then(writeRawCache).catch(() => {}); // fire-and-forget revalidate
    return Promise.resolve(_cache);
  }

  _loading = fetchRaw().then(raw => {
    writeRawCache(raw);
    _cache = buildGameData(raw);
    _loading = null;
    return _cache;
  });
  return _loading;
}

// ── React hook ─────────────────────────────────────────────────────────────────

export function useGameData(): { data: GameData | null; loading: boolean; error: string | null } {
  const [data, setData]       = useState<GameData | null>(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (_cache) { setData(_cache); setLoading(false); return; }
    let alive = true;
    loadGameData()
      .then(d  => { if (alive) { setData(d);         setLoading(false); } })
      .catch(e => { if (alive) { setError(String(e)); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return { data, loading, error };
}
